import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Clock3, LoaderCircle, MessageSquareText, Radio, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SmsDiagnosticStage, SmsDiagnostics } from "../../../shared/sms-diagnostics";

type AdminSmsDiagnosticsProps = {
  userId: string;
};

function formatTimestamp(value: string | null) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

async function getDiagnostics(userId: string, recipientId: string) {
  const params = new URLSearchParams();
  if (recipientId) params.set("recipientId", recipientId);
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : "";
  let response: Response;

  try {
    response = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/sms-diagnostics${suffix}`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Could not reach SMS diagnostics. Check the connection and try again.");
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error("Your admin session cannot access these diagnostics. Sign in again, then retry.");
    }
    if (response.status === 404) {
      throw new Error("SMS diagnostics are not available for this user.");
    }
    throw new Error(`SMS diagnostics could not be loaded (HTTP ${response.status}). Try again.`);
  }

  return response.json() as Promise<SmsDiagnostics>;
}

function StageRow({ label, stage }: { label: string; stage: SmsDiagnosticStage }) {
  return (
    <section className="rounded-md border bg-background/70 p-3" aria-label={label}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{label}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{stage.status || "No status recorded"}</p>
        </div>
        <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
          {stage.code || "No code"}
        </span>
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <div>
          <dt className="text-muted-foreground">Timestamp</dt>
          <dd className="mt-0.5 break-words">{formatTimestamp(stage.at)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Recommended action</dt>
          <dd className="mt-0.5 break-words">{stage.action || "No action supplied"}</dd>
        </div>
      </dl>
    </section>
  );
}

export default function AdminSmsDiagnostics({ userId }: AdminSmsDiagnosticsProps) {
  const [selectedRecipientId, setSelectedRecipientId] = useState("");
  const [lastDiagnostics, setLastDiagnostics] = useState<SmsDiagnostics | null>(null);
  const query = useQuery<SmsDiagnostics, Error>({
    queryKey: ["/api/admin/users", userId, "sms-diagnostics", selectedRecipientId || "all"],
    queryFn: () => getDiagnostics(userId, selectedRecipientId),
    enabled: false,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data) return;
    setLastDiagnostics(query.data);
    if (!selectedRecipientId && query.data.recipients.length > 0) {
      setSelectedRecipientId(String(query.data.recipients[0].id));
    }
  }, [query.data, selectedRecipientId]);

  const diagnostics = query.data ?? lastDiagnostics;
  const recipientName = diagnostics?.recipients.find(
    (recipient) => recipient.id === diagnostics?.recipientId,
  )?.maskedPhone;
  const handleCheck = () => {
    if (!query.isFetching) void query.refetch();
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquareText className="h-4 w-4 text-muted-foreground" />
              SMS Diagnostics
            </CardTitle>
            <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
              Trace a missing Agent reply across account eligibility, inbound handling, the app queue, and delivery.
            </p>
          </div>
          <Button size="sm" onClick={handleCheck} disabled={query.isFetching} className="shrink-0">
            {query.isFetching ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Radio className="mr-2 h-4 w-4" />
            )}
            {query.isFetching ? "Checking…" : diagnostics ? "Check again" : "Check SMS diagnostics"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {query.error && (
          <div role="alert" className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Diagnostics check failed</p>
              <p className="mt-0.5">{query.error.message}</p>
            </div>
          </div>
        )}

        {!diagnostics && !query.isFetching && !query.error && (
          <div className="rounded-md border border-dashed bg-muted/30 px-4 py-5 text-sm">
            <p className="font-medium">No diagnostic check has been run.</p>
            <p className="mt-1 text-muted-foreground">
              Run a check to inspect the latest SMS path. No messages are sent by this tool.
            </p>
          </div>
        )}

        {diagnostics && (
          <>
            {diagnostics.recipients.length > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="min-w-0 flex-1 text-sm">
                  <span className="mb-1 block font-medium">Verified recipient</span>
                  <select
                    value={selectedRecipientId}
                    onChange={(event) => setSelectedRecipientId(event.target.value)}
                    disabled={query.isFetching}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {diagnostics.recipients.map((recipient) => (
                      <option key={recipient.id} value={recipient.id}>
                        {recipient.maskedPhone}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="pb-2 text-xs text-muted-foreground">
                  Select a masked number, then run a check for that recipient.
                </p>
              </div>
            )}

            {diagnostics.recipients.length === 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
                <p className="font-medium">No verified recipients returned</p>
                <p className="mt-1 text-muted-foreground">
                  Recipient-specific checks are unavailable. Eligibility and account-level readiness are still shown below.
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-y py-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-3.5 w-3.5" />
                Checked {formatTimestamp(diagnostics.checkedAt)}
              </span>
              {recipientName && <span>Recipient: {recipientName}</span>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <section className="rounded-md border bg-background/70 p-3" aria-label="SMS eligibility">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold">Eligibility</h3>
                  <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${diagnostics.eligibility.eligible ? "border-emerald-600/30 text-emerald-700 dark:text-emerald-400" : "border-amber-600/30 text-amber-700 dark:text-amber-400"}`}>
                    {diagnostics.eligibility.eligible && <Check className="h-3 w-3" />}
                    {diagnostics.eligibility.eligible ? "Eligible" : "Not eligible"}
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Opted out: <span className="text-foreground">{diagnostics.eligibility.optedOut ? "Yes" : "No"}</span>
                </p>
                <p className="mt-2 text-sm">{diagnostics.eligibility.action || "No action supplied"}</p>
              </section>
              <StageRow label="Readiness" stage={diagnostics.readiness} />
              <StageRow label="Provider inbound" stage={diagnostics.inbound} />
              <StageRow label="App queue" stage={diagnostics.queue} />
              <div className="sm:col-span-2">
                <StageRow label="Outgoing delivery" stage={diagnostics.delivery} />
              </div>
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground">
              Diagnostic records are retained for seven days. Provider evidence is bounded and may not include every carrier event; absence of an event here does not prove it never occurred.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
